import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from "@/lib/router-dom/index";
import usePage from '@/hooks/use-page/index';
import Breadcrumbs from '@/componentsDS/Breadcrumbs/index';
import useTermClause from '@/hooks/use-term-clause/index';
import moment from 'moment'
import Icon from '@/componentsDS/Icon/Icon';
import { IconSize, IconStyle } from '@/componentsDS/Icon/Icon.type';
import Button from '@/componentsDS/Button/Button';
import { ButtonSize, ButtonType, ButtonVariant } from '@/componentsDS/Button/Button.type';
import useModal from '@/hooks/use-modal/index';
import { EditModelTermModal } from './modals/edit-model-term-modal';
import ModelTermForm from './components/model-term-form';
import CardConfigure from '@/componentsDS/Modal/CardConfigure';
import parse from "html-react-parser";
import Loader from '@/componentsDS/Loader/Loader';
import Toast from '@/components/Toast/index';
import AccordionCardConfigure from '@/components/CardConfigureAccordian/index';
import { featureConstant } from '@/constants/configuration/feature';
import { useFeature } from "@/hooks/use-feature/index.js";
import { renderUserEmailInline } from "@/utils/formatUserEmail";

const replaceableContent = (domNode, existingValues) => {
	if (
		domNode.name === "span" &&
		domNode.attribs?.class?.includes("replaceable")
	) {
		const id = domNode.attribs.id;
		return <span>{existingValues[id]}</span>;
	}
};

const ModelTermEdit = () => {
	const isSoftDeleteUserEnabled = useFeature(featureConstant.SOFT_DELETE_USER);
	const [isLoading, setIsLoading] = useState(false);
	const [isEditTermClause, setIsEditTermClause] = useState(false)
	const { getTermClause, termClause, termClauseLoading, updateTermClauseName, toastList } = useTermClause();
	const {show: showRenameModal, hide: hideRenameModal, visible: isRenameModalVisible} = useModal();
	const { goTo } = useNavigate();
	const { clauseId } = useParams();

	const useCases = sessionStorage.getItem("useCases");
	const defaultBreadCrumbs = {
		title: "Edit Clause",
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: "Account Settings",
				link: `/setting/account`
			},
      {
        label: termClause?.clauseName,
        link: `/setting/edit-clause`
      }
		],
	};

  	usePage(defaultBreadCrumbs);
 
	const modelTermEnabled = useMemo(() => {
		if(useCases) {
			const parsedUseCases = JSON.parse(useCases)
			return parsedUseCases.some(
				(useCase) =>
					useCase.enableETR && useCase.etrIssuance?.contracts?.length > 0
			);
		}
		return false
	}, [useCases]);

	// if modelTermEnabled is false, re-route to /setting/account
	useEffect(() => {
		if(!modelTermEnabled) {
			goTo("/setting/account")
		}
	}, [modelTermEnabled])


	const getClause = useCallback(async () => {
			await getTermClause(clauseId);
			setIsLoading(false);
	}, [clauseId]);


  useEffect(() => {
		if (modelTermEnabled) {
			setIsLoading(true);
			getClause();
		}
  }, [modelTermEnabled]);


  const onRenameClauseHandler = async (clauseName) => {
	  	const data = await updateTermClauseName(termClause.id, { clauseName });
		if(data) {
			hideRenameModal();
			await getTermClause(termClause.id);
		}
  };

  const formatData = (message, date, by) => {
    const formattedDate = moment(date).format('DD/MM/YYYY h:mm A');
    const userInfo = isSoftDeleteUserEnabled ? renderUserEmailInline(by) : by;
    return <>
		{message} {userInfo} on {formattedDate}
	</>
  }

  
  const renderSections = useMemo(() => {
		return Object.entries(termClause?.template.elementSchema.properties ?? {}).map(
			([key, value]) => {
				const existingValues = termClause?.clausePayload[key]
				const innerHtml = termClause?.template.templateText[key].replaceAll(String.raw`\n`, '\n').replaceAll(String.raw`\"`, '"');
				return (
					<AccordionCardConfigure key={key} addHeaderClass={"account-settings__model-term-edit__card-configure"}>
						<CardConfigure.Header>
							<CardConfigure.Header.Title>
								<div className="account-settings__create-modal-term__section__name">{value.name}</div>
							</CardConfigure.Header.Title>
						</CardConfigure.Header>
						<CardConfigure.Body>
							<CardConfigure.Body.Col>
								<div>{parse(innerHtml, { replace: (domNode) => replaceableContent(domNode, existingValues)})}</div>
							</CardConfigure.Body.Col>
						</CardConfigure.Body>
					</AccordionCardConfigure>
				);
			}
		);
	}, [termClause]);

  const toggleModelTermHandler = async (status) => {
		setIsEditTermClause(status);
		await getClause();
  };


  if (isEditTermClause && termClause) {
		return (
			<ModelTermForm
				isEdit={isEditTermClause}
        		termClause={termClause}
				termTemplate={termClause?.template}
				setCreateModelTerm={toggleModelTermHandler}
			/>
		);
  }

  return (
		<>
			<Breadcrumbs breadcrumbItems={defaultBreadCrumbs.breadcrumbs} />
			{isLoading || termClauseLoading ? (
				<div className="notification-list__loader-icon">
					<Loader />
				</div>
			) : (
				<>
					<div className="account-settings__edit-term-clause__header">
						<div>
							<Button
								onClick={() => goTo("/setting/account")}
								text={
									<Icon
										name="chevron-left"
										size={IconSize.MEDIUM}
										style={IconStyle.OUTLINED}
									/>
								}
								type={ButtonType.UTILITY}
								variant={ButtonVariant.UTILITY}
							/>
							<h2 className="account-settings__edit-term-clause__header-title">
								{termClause?.clauseName}
							</h2>
							<Button
								onClick={showRenameModal}
								text={
									<Icon
										name="edit-pen"
										size={IconSize.SMALL}
										style={IconStyle.OUTLINED}
									/>
								}
								type={ButtonType.UTILITY}
								variant={ButtonVariant.UTILITY}
							/>
						</div>
						<Button
							text="Edit Template"
							type={ButtonType.SOLID}
							variant={ButtonVariant.STANDARD}
							size={ButtonSize.DEFAULT}
							onClick={() => setIsEditTermClause(true)}
							className="account-settings__modal_term-action"
						/>
					</div>
					<div className="account-settings__edit-term-clause__header-date">
						{formatData(
							"Created by",
							termClause?.created,
							termClause?.createdBy
						)}
					</div>
					<div className="account-settings__edit-term-clause__header-date">
						{formatData(
							"Last modified by",
							termClause?.modified,
							termClause?.modifiedBy
						)}
					</div>
					<hr />
					{renderSections}
				</>
			)}
			<EditModelTermModal
				isModalVisible={isRenameModalVisible}
				onEditHandler={onRenameClauseHandler}
				hideModal={hideRenameModal}
				name={termClause?.clauseName}
			/>
			<Toast toastList={toastList}/>
		</>
  );
}


export default ModelTermEdit

